import { irrigationErrors } from "../../utils/irrigation.js";

export const GHG_CSV_HEADERS = [
  "client_reference",
  "crop_year",
  "crop",
  "acres",
  "province_code",
  "latitude",
  "longitude",
  "postal_code",
  "municipality",
  "yield_value",
  "yield_unit",
  "tillage_practice",
  "previous_crop",
  "residue_management",
  "soil_organic_matter_percent",
  "fertilizers_json",
  "operations_json",
  "irrigation_water_applied",
  "irrigation_water_unit",
  "irrigation_energy_source",
  "irrigation_energy_used",
  "irrigation_energy_unit",
];

function escapeCsv(value) {
  const text = value == null ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const TEMPLATE_ROW = [
  "north-2026",
  2026,
  "CANOLA",
  80,
  "MB",
  49.8951,
  -97.1384,
  "R3C 0V8",
  "Winnipeg",
  40,
  "bu/ac",
  "NoTill",
  "spring wheat",
  "retained",
  4.2,
  JSON.stringify([{
    productName: "Urea",
    applicationRate: 100,
    rateUnit: "lb/ac",
    nitrogenPercent: 46,
    phosphorusPercent: 0,
    potassiumPercent: 0,
    applicationDate: "2026-05-10",
    applicationMethod: "Broadcast",
    hasNitrificationInhibitor: true,
  }]),
  JSON.stringify([{
    operationType: "Planting",
    operationDate: "2026-05-15",
    equipmentType: "Air seeder",
    fuelUsedLitres: 42,
  }]),
  2,
  "in",
  "Electricity",
  125,
  "kWh",
];

export const GHG_CSV_TEMPLATE = [
  GHG_CSV_HEADERS.join(","),
  TEMPLATE_ROW.map(escapeCsv).join(","),
].join("\n");

function parseRows(text) {
  const rows = [];
  let row = [];
  let value = "";
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    if (char === '"') {
      if (quoted && next === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === "," && !quoted) {
      row.push(value);
      value = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(value);
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = [];
      value = "";
    } else {
      value += char;
    }
  }
  if (quoted) throw new Error("The CSV contains an unclosed quoted value.");
  row.push(value);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows;
}

function validateNumber(value, label, { min, max, required = true }) {
  if (String(value).trim() === "") return required ? `${label} is required` : null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${label} must be a number`;
  if (numeric < min || numeric > max) return `${label} must be between ${min} and ${max}`;
  return null;
}

function numberOrNull(value) {
  return String(value).trim() === "" ? null : Number(value);
}

function validateLength(value, label, maximum, errors) {
  if (value != null && String(value).length > maximum) errors.push(`${label} must be ${maximum} characters or fewer`);
}

function validateObjectKeys(item, allowedKeys, label, errors) {
  const unknown = Object.keys(item).filter((key) => !allowedKeys.includes(key));
  if (unknown.length) errors.push(`${label} contains unknown field${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}`);
}

function validateIsoDate(value, label, errors) {
  if (value == null || value === "") return;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value)) || Number.isNaN(Date.parse(`${value}T00:00:00Z`))) {
    errors.push(`${label} must use YYYY-MM-DD`);
  }
}

function parseJsonArray(value, label, errors) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) throw new Error();
    return parsed;
  } catch {
    errors.push(`${label} must be a valid JSON array`);
    return [];
  }
}

function validateFertilizers(items, capabilities, errors) {
  const allowedKeys = ["productName", "applicationRate", "rateUnit", "nitrogenPercent", "phosphorusPercent", "potassiumPercent", "applicationDate", "applicationMethod", "hasNitrificationInhibitor"];
  if (items.length > (capabilities?.maxFertilizers || 25)) errors.push("Too many fertilizer rows");
  items.forEach((item, index) => {
    const prefix = `Fertilizer ${index + 1}`;
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      errors.push(`${prefix} must be an object`);
      return;
    }
    validateObjectKeys(item, allowedKeys, prefix, errors);
    if (!String(item.productName || "").trim()) errors.push(`${prefix} product name is required`);
    validateLength(item.productName, `${prefix} product name`, 150, errors);
    [
      validateNumber(item.applicationRate, `${prefix} application rate`, { min: 0, max: 10000 }),
      validateNumber(item.nitrogenPercent, `${prefix} nitrogen percent`, { min: 0, max: 100 }),
      validateNumber(item.phosphorusPercent, `${prefix} phosphorus percent`, { min: 0, max: 100 }),
      validateNumber(item.potassiumPercent, `${prefix} potassium percent`, { min: 0, max: 100 }),
    ].filter(Boolean).forEach((error) => errors.push(error));
    if (!(capabilities?.fertilizerRateUnits || []).includes(item.rateUnit)) errors.push(`${prefix} rate unit is not supported`);
    validateIsoDate(item.applicationDate, `${prefix} application date`, errors);
    validateLength(item.applicationMethod, `${prefix} application method`, 100, errors);
    if (item.hasNitrificationInhibitor != null && typeof item.hasNitrificationInhibitor !== "boolean") {
      errors.push(`${prefix} inhibitor value must be true or false`);
    }
  });
}

function validateOperations(items, capabilities, errors) {
  const allowedKeys = ["operationType", "operationDate", "equipmentType", "fuelUsedLitres"];
  if (items.length > (capabilities?.maxOperations || 25)) errors.push("Too many operation rows");
  items.forEach((item, index) => {
    const prefix = `Operation ${index + 1}`;
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      errors.push(`${prefix} must be an object`);
      return;
    }
    validateObjectKeys(item, allowedKeys, prefix, errors);
    if (!String(item.operationType || "").trim()) errors.push(`${prefix} type is required`);
    validateLength(item.operationType, `${prefix} type`, 100, errors);
    validateIsoDate(item.operationDate, `${prefix} date`, errors);
    validateLength(item.equipmentType, `${prefix} equipment type`, 100, errors);
    const fuelError = validateNumber(item.fuelUsedLitres, `${prefix} fuel`, { min: 0, max: 10000, required: false });
    if (fuelError) errors.push(fuelError);
  });
}

export function parseGhgCalculatorCsv(text, capabilities) {
  const rows = parseRows(String(text || "").replace(/^\uFEFF/, ""));
  if (!rows.length) throw new Error("The CSV is empty.");

  const headers = rows[0].map((header) => header.trim().toLowerCase());
  if (new Set(headers).size !== headers.length) throw new Error("CSV headers must be unique.");
  if (headers.length !== GHG_CSV_HEADERS.length || headers.some((header, index) => header !== GHG_CSV_HEADERS[index])) {
    throw new Error(`Use the exact template headers: ${GHG_CSV_HEADERS.join(", ")}`);
  }

  const dataRows = rows.slice(1);
  const maxRows = capabilities?.maxCsvRows || 10;
  if (!dataRows.length) throw new Error("Add at least one calculation row.");
  if (dataRows.length > maxRows) throw new Error(`CSV files are limited to ${maxRows} rows.`);

  const references = new Set();
  return dataRows.map((cells, rowIndex) => {
    const values = Object.fromEntries(headers.map((header, index) => [header, (cells[index] || "").trim()]));
    const errors = [];
    if (cells.length !== headers.length) errors.push(`Expected ${headers.length} columns`);
    if (!values.client_reference) errors.push("Client reference is required");
    if (values.client_reference.length > 64) errors.push("Client reference must be 64 characters or fewer");
    if (references.has(values.client_reference)) errors.push("Client reference is duplicated");
    references.add(values.client_reference);

    [
      validateNumber(values.crop_year, "Crop year", { min: 1900, max: 2200 }),
      validateNumber(values.acres, "Acres", { min: 0.01, max: 1000000 }),
      validateNumber(values.latitude, "Latitude", { min: -90, max: 90, required: false }),
      validateNumber(values.longitude, "Longitude", { min: -180, max: 180, required: false }),
      validateNumber(values.yield_value, "Yield", { min: 0, max: 100000 }),
      validateNumber(values.soil_organic_matter_percent, "Soil organic matter", { min: 0, max: 100, required: false }),
    ].filter(Boolean).forEach((error) => errors.push(error));

    if (values.crop_year && !Number.isInteger(Number(values.crop_year))) errors.push("Crop year must be a whole number");
    validateLength(values.postal_code, "Postal code", 20, errors);
    validateLength(values.municipality, "Municipality", 100, errors);
    validateLength(values.previous_crop, "Previous crop", 100, errors);
    validateLength(values.residue_management, "Residue management", 100, errors);

    if (Boolean(values.latitude) !== Boolean(values.longitude)) errors.push("Latitude and longitude must be supplied together");
    if (!(capabilities?.supportedCrops || []).includes(values.crop)) errors.push("Crop is not supported");
    if (!(capabilities?.provinceCodes || []).includes(values.province_code)) errors.push("Province code is not supported");
    if (!(capabilities?.yieldUnits || []).includes(values.yield_unit)) errors.push("Yield unit is not supported");
    if (values.tillage_practice && !(capabilities?.tillagePractices || []).includes(values.tillage_practice)) errors.push("Tillage practice is not supported");

    const fertilizers = parseJsonArray(values.fertilizers_json, "Fertilizers JSON", errors);
    const operations = parseJsonArray(values.operations_json, "Operations JSON", errors);
    validateFertilizers(fertilizers, capabilities, errors);
    validateOperations(operations, capabilities, errors);

    const hasIrrigation = [
      values.irrigation_water_applied,
      values.irrigation_water_unit,
      values.irrigation_energy_source,
      values.irrigation_energy_used,
      values.irrigation_energy_unit,
    ].some(Boolean);
    let irrigation = null;
    if (hasIrrigation) {
      [
        validateNumber(values.irrigation_water_applied, "Irrigation water applied", { min: 0, max: 1e12 }),
        validateNumber(values.irrigation_energy_used, "Irrigation energy used", { min: 0, max: 10000000, required: false }),
      ].filter(Boolean).forEach((error) => errors.push(error));
      if (!(capabilities?.irrigationWaterUnits || []).includes(values.irrigation_water_unit)) errors.push("Irrigation water unit is not supported");
      if (Boolean(values.irrigation_energy_used) !== Boolean(values.irrigation_energy_unit)) errors.push("Irrigation energy used and unit must be supplied together");
      validateLength(values.irrigation_energy_source, "Irrigation energy source", 100, errors);
      validateLength(values.irrigation_energy_unit, "Irrigation energy unit", 30, errors);
      irrigation = {
        waterApplied: numberOrNull(values.irrigation_water_applied),
        waterUnit: values.irrigation_water_unit,
        energySource: values.irrigation_energy_source || null,
        energyUsed: numberOrNull(values.irrigation_energy_used),
        energyUnit: values.irrigation_energy_unit || null,
      };
      errors.push(...Object.values(irrigationErrors(irrigation, { complete: true, acres: values.acres })));
    }

    return {
      rowNumber: rowIndex + 2,
      errors,
      payload: {
        contractVersion: "2.0",
        clientReference: values.client_reference,
        cropYear: Number(values.crop_year),
        crop: values.crop,
        acres: Number(values.acres),
        location: {
          provinceCode: values.province_code,
          latitude: numberOrNull(values.latitude),
          longitude: numberOrNull(values.longitude),
          postalCode: values.postal_code || null,
          municipality: values.municipality || null,
        },
        production: {
          yield: Number(values.yield_value),
          yieldUnit: values.yield_unit,
          tillagePractice: values.tillage_practice || null,
          previousCrop: values.previous_crop || null,
          residueManagement: values.residue_management || null,
          soilOrganicMatterPercent: numberOrNull(values.soil_organic_matter_percent),
        },
        fertilizers,
        operations,
        irrigation,
      },
    };
  });
}

export function buildGhgResultsCsv(jobs) {
  const headers = [
    "client_reference",
    "status",
    "total_kg_co2e",
    "kg_co2e_per_acre",
    "kg_co2e_per_hectare",
    "model_name",
    "model_version",
    "wrapper_name",
    "wrapper_version",
    "calculated_utc",
    "error",
  ];
  const lines = jobs.map((job) => {
    const emissions = job.result?.emissions || {};
    return [
      job.clientReference,
      job.status,
      emissions.totalKgCo2e,
      emissions.kgCo2ePerAcre,
      emissions.kgCo2ePerHectare,
      job.modelName,
      job.modelVersion,
      job.wrapperName,
      job.wrapperVersion,
      job.calculatedUtc,
      job.error,
    ].map(escapeCsv).join(",");
  });
  return [headers.join(","), ...lines].join("\n");
}
